import { useEffect, useState } from 'react'

import {
  api,
  descargarDocumento,
  ErrorApi,
  type CotizacionGuardada,
  type ResultadoCotizacion as ResultadoEstandarGuardado,
  type ResultadoOffgrid as ResultadoOffgridGuardado,
} from '../api'
import { fechaCorta } from '../formato'
import { EstadoVersion } from './EstadoVersion'
import { ResultadoCotizacion } from './ResultadoCotizacion'
import { ResultadoOffgrid } from './ResultadoOffgrid'

interface Props {
  cotizacionId: string
  alVolver: () => void
  alNuevaVersion: (base: CotizacionGuardada) => void
}

export function DetalleCotizacion({ cotizacionId, alVolver, alNuevaVersion }: Props) {
  const [cotizacion, setCotizacion] = useState<CotizacionGuardada | null>(null)
  const [error, setError] = useState('')
  const [generando, setGenerando] = useState<'' | 'proforma' | 'informe'>('')
  // Que lleva el PDF. Por defecto todo; se desmarca lo que no se le quiere mostrar a este cliente.
  const [conRetorno, setConRetorno] = useState(true)
  const [conEspera, setConEspera] = useState(true)
  // Apagado por defecto: la proforma de siempre no desglosa precios por item.
  const [conDetalle, setConDetalle] = useState(false)

  useEffect(() => {
    api
      .cotizacion(cotizacionId)
      .then(({ cotizacion }) => setCotizacion(cotizacion))
      .catch((err) => setError(err instanceof ErrorApi ? err.message : 'No se pudo cargar.'))
  }, [cotizacionId])

  const descargar = async (tipo: 'proforma' | 'informe') => {
    setError('')
    setGenerando(tipo)
    try {
      // El informe financiero es todo analisis: solo la ultima pagina es opcional ahi.
      await descargarDocumento(cotizacionId, tipo, {
        retorno: tipo === 'proforma' ? conRetorno : true,
        espera: conEspera,
        detalle: tipo === 'proforma' && cotizacion?.tipo === 'estandar' && conDetalle,
      })
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo generar el documento.')
    } finally {
      setGenerando('')
    }
  }

  return (
    <section>
      <button className="enlace volver" onClick={alVolver}>
        ← Volver al cliente
      </button>
      {cotizacion && (
        <>
          <div className="encabezado">
            <div>
              <h1>
                Versión {cotizacion.version}{' '}
                <span className={`insignia ${cotizacion.tipo === 'industrial' ? 'insignia-industrial' : 'insignia-web'}`}>
                  {cotizacion.tipo === 'offgrid'
                    ? cotizacion.potencia_kwp > 0
                      ? 'Off-grid'
                      : 'Respaldo sin paneles'
                    : cotizacion.tipo === 'industrial'
                      ? 'Industrial trifásico'
                      : cotizacion.con_bateria
                        ? 'Híbrido'
                        : 'On-grid'}
                </span>
              </h1>
              <p className="suave">
                {fechaCorta(cotizacion.created_at)} · por {cotizacion.creado_por_nombre}
                {cotizacion.nota && ` · «${cotizacion.nota}»`}
              </p>
            </div>
            <div className="botones">
              <button className="secundario" onClick={() => alNuevaVersion(cotizacion)}>
                Nueva versión a partir de esta
              </button>
              {cotizacion.tipo !== 'offgrid' && (
                <button className="secundario" onClick={() => descargar('informe')} disabled={generando !== ''}>
                  {generando === 'informe' ? 'Generando…' : 'Informe financiero (PDF)'}
                </button>
              )}
              <button className="primario" onClick={() => descargar('proforma')} disabled={generando !== ''}>
                {generando === 'proforma' ? 'Generando…' : 'Generar proforma (PDF)'}
              </button>
            </div>
          </div>
          {cotizacion.tipo !== 'offgrid' && (
            <div className="tarjeta opciones-pdf">
              <strong>El PDF lleva:</strong>
              <label className="casilla">
                <input type="checkbox" checked={conRetorno} onChange={(e) => setConRetorno(e.target.checked)} /> Retorno de la inversión (ROI y gráfico)
              </label>
              {cotizacion.tipo === 'estandar' && (
                <label className="casilla">
                  <input type="checkbox" checked={conDetalle} onChange={(e) => setConDetalle(e.target.checked)} /> Detalle de ítems con sus precios
                </label>
              )}
              {cotizacion.entrada.espera && (
                <label className="casilla">
                  <input type="checkbox" checked={conEspera} onChange={(e) => setConEspera(e.target.checked)} /> Última página: consumo proyectado y costo de esperar
                </label>
              )}
            </div>
          )}
          {error && <p className="error">{error}</p>}
          <EstadoVersion cotizacion={cotizacion} alCambiar={setCotizacion} />
          {cotizacion.tipo === 'offgrid' ? (
            <ResultadoOffgrid resultado={cotizacion.resultado as ResultadoOffgridGuardado} />
          ) : (
            <ResultadoCotizacion resultado={cotizacion.resultado as ResultadoEstandarGuardado} />
          )}
        </>
      )}
      {!cotizacion && error && <p className="error">{error}</p>}
    </section>
  )
}
